import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  generatePath,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useBreadcrumbs } from "../../context/BreadcrumbsContext";
import { usePageMetadata } from "../../hooks/usePageMetadata";
import { routes } from "../../routes";
import { Button, Container, Stack } from "@mui/material";
import NewModal from "./NewQuoteModal";
import { useApiRequest } from "../../hooks/useApiRequest";
import { Series } from "../../types/series";
import { MissingDataModal } from "./MissingDataModal";
import { PageHeader } from "../../components/PageHeader";
import FilterSearch from "./FilterSearchPanel";
import { EmptyState } from "../../components/EmptyState";
import {
  Order,
  Prospect,
} from "../../types/order";
import { ProspectKanbanView } from "./ProspectKanbanView";
import { DateTime } from "luxon";
import { DeleteOrderModal } from "../../components/DeleteOrderModal";
import { useSession } from "../../hooks/useSession";
import { useDetectMobile } from "../../hooks/useDetectMobile";

type ProspectFilter = {
  searchText: string;
  startDate: DateTime | null;
  endDate: DateTime | null;
  usingLastUpdated: boolean;
};

const ProspectsRoute: React.FC = () => {
  const isMobile = useDetectMobile();
  const navigate = useNavigate();
  const { user } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenantGuid } = useParams();
  const [isCreating, setIsCreating] = useState(false);
  const [convertingProspectGuid, setConvertingProspectGuid] =
    useState<string>();
  const [showMissingDataModal, setShowMissingDataModal] = useState(false);
  const [isGridView, setIsGridView] = useState(
    () =>
      !isMobile &&
      new URLSearchParams(window.location.search).get("view") !== "list"
  );

  useEffect(() => {
    setSearchParams((prev) => {
      prev.set("view", isGridView ? "kanban" : "list");
      return prev;
    });
  }, [isGridView, setSearchParams, searchParams]);

  // Filtering
  const [filters, setFilters] = useState<ProspectFilter>({
    searchText: "",
    startDate: null,
    endDate: null,
    usingLastUpdated: true,
  });

  const [deletingOrderGuid, setDeletingOrderGuid] = useState<string | null>(
    null
  );

  const { setBreadcrumbs } = useBreadcrumbs();

  usePageMetadata({ title: routes.prospects.label });

  useEffect(() => {
    setBreadcrumbs([{ label: routes.prospects.label }]);
  }, [setBreadcrumbs]);

  const {
    data: seriesData,
    loading: seriesLoading,
    request: seriesRequest,
  } = useApiRequest<Series[]>();

  const { loading: newProspectLoading, request: newProspectRequest } =
    useApiRequest<Prospect>();

  const { data: orderData, request: orderRequest } =
    useApiRequest<Order[]>(false);

  const refreshOrders = useCallback(() => {
    orderRequest("/prospects", { method: "GET" });
  }, [orderRequest]);

  const [usingLastUpdated, setUsingLastUpdated] = useState(localStorage.getItem("usingLastUpdated-selected") === "true");

  const filteredOrders = useMemo(() => {
    if (!orderData) {
      return [];
    }
    return orderData.filter((order) => {
      const st = filters.searchText.toLowerCase();
      const dateFormat = "yyyy/MM/dd";
      const textFilter =
        order.orderIdentifier.toLowerCase().includes(st) ||
        (order.buyerLastName ?? "").toLowerCase().includes(st) ||
        (order.buyerFirstName ?? "").toLowerCase().includes(st)
        ;

      const startDateFilter =
        !filters.startDate ||
        DateTime.fromSeconds(order?.createdTimestamp).toFormat(dateFormat) >=
          filters.startDate.toFormat(dateFormat);

      const endDateFilter =
        !filters.endDate ||
        DateTime.fromSeconds(order?.createdTimestamp).toFormat(dateFormat) <=
          filters.endDate.toFormat(dateFormat);

      return (
        startDateFilter &&
        endDateFilter &&
        textFilter
      );
    }).sort((a,b) => {
      return usingLastUpdated ? b.updatedTimestamp - a.updatedTimestamp : 0
    });
  }, [orderData, filters, usingLastUpdated]);

  const deletingOrder = useMemo(() => {
    return orderData?.find((o) => o.orderGuid === deletingOrderGuid);
  }, [deletingOrderGuid, orderData]);

  useEffect(() => {
    refreshOrders();
  }, [refreshOrders]);

  const hasSeries = useMemo(() => {
    if (user?.isRetailUser) {
      return true;
    }
    if (!seriesData || seriesData.length === 0) {
      return false;
    }
    return seriesData.some((series) => series?.models.length > 0);
  }, [seriesData, user?.isRetailUser]);

  useEffect(() => {
    if (seriesData) {
      if (hasSeries) {
        setIsCreating(true);
      } else {
        setShowMissingDataModal(true);
      }
    }
  }, [seriesData, hasSeries]);

  const handleNewQuoteClick = useCallback(() => {
    seriesRequest("/quotes/series", {
      method: "GET",
    });
  }, [seriesRequest]);

  const handleNewProspectClick = useCallback(() => {
    newProspectRequest(
      "/retailer/addprospect",
      {
        method: "GET",
      },
      {
        onSuccess: (data) => {
          if (!user?.tenantGuid) {
            return;
          }
          navigate(
            generatePath(routes.quotesAndOrdersDetail.path, {
              tenantGuid: user?.tenantGuid,
              orderGuid: data.orderGuid,
            })
          );
        },
      }
    );
  }, [newProspectRequest, navigate, user?.tenantGuid]);

  const handleSearchTextChange = useCallback((searchText: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      searchText,
    }));
  }, []);

  const handleStartDateSelection = useCallback((startDate: DateTime | null) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      startDate,
    }));
  }, []);

  const handleEndDateSelection = useCallback((endDate: DateTime | null) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      endDate,
    }));
  }, []);

  const handleOrderEditClick = useCallback(
    (orderGuid: string) => {
      navigate(
        `${generatePath(routes.quotesAndOrdersDetail.path, {
          tenantGuid: tenantGuid ?? "",
          orderGuid,
        })}?backTo=${isGridView ? "kanban" : "list"}`
      );
    },
    [navigate, tenantGuid, isGridView]
  );

  const handleDeleteOrderClick = (orderGuid: string) => {
    setDeletingOrderGuid(orderGuid);
  };

  const handleDeleteOrderClose = () => {
    setDeletingOrderGuid(null);
  };

  const handleDeleteOrderSuccess = () => {
    handleDeleteOrderClose();
    refreshOrders();
  };

  const handleConvertToQuoteClick = (prospectGuid: Prospect["orderGuid"]) => {
    setIsCreating(true);
    setConvertingProspectGuid(prospectGuid);
  };

  const handleUsingLastUpdatedClick = () => {
    const newValue = !usingLastUpdated;
    localStorage.setItem("usingLastUpdated-selected",newValue ? "true" : "false");
    setUsingLastUpdated(newValue);
  };

  return (
    <>
      <Container maxWidth={false}>
        <PageHeader
          title="Prospects"
          actions={
            <Stack direction="row" spacing={1}>
              {user?.isRetailUser && (
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={seriesLoading || newProspectLoading}
                  onClick={handleNewProspectClick}
                  sx={{
                    color: "primary.main",
                    backgroundColor: "primary.light",
                    ":hover": { backgroundColor: "secondary.main" },
                    border: "none",
                  }}
                >
                  + New Prospect
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                disabled={seriesLoading || newProspectLoading}
                onClick={handleNewQuoteClick}
              >
                + New Quote
              </Button>
            </Stack>
          }
        />
        <FilterSearch
          isGridView={isGridView}
          onViewChange={(isGridView) => setIsGridView(isGridView)}
          onSearchTextChange={handleSearchTextChange}
          // Date
          onStartDateSelect={handleStartDateSelection}
          onEndDateSelect={handleEndDateSelection}
          // Last Updated
          usingLastUpdated={usingLastUpdated}
          onUsingLastUpdatedClicked={handleUsingLastUpdatedClick}
        />
      </Container>
      {isCreating && (
        <NewModal
          isOpen={isCreating}
          onClose={() => setIsCreating(false)}
          prospectGuid={convertingProspectGuid}
          onSaveSuccessful={(order) => {
            setIsCreating(false);
            navigate(
              generatePath(routes.quotesAndOrdersDetail.path, {
                tenantGuid: tenantGuid ?? "",
                orderGuid: order.orderGuid,
              })
            );
          }}
        />
      )}
      {showMissingDataModal && (
        <MissingDataModal onClose={() => setShowMissingDataModal(false)} />
      )}
      {orderData && orderData.length === 0 ? (
        <EmptyState
          heading="No Prospects"
          body="To create a prospect, click the New Prospect Button"
        />
      ) : (
        <ProspectKanbanView
          orders={filteredOrders || []}
          refreshOrders={refreshOrders}
          handleOrderEditClick={handleOrderEditClick}
          handleDeleteOrderClick={handleDeleteOrderClick}
          handleConvertToQuoteClick={handleConvertToQuoteClick}
          usingLastUpdated={usingLastUpdated}
        />
      )}
      {deletingOrder && (
        <DeleteOrderModal
          order={deletingOrder}
          onDeleteSuccess={handleDeleteOrderSuccess}
          onClose={handleDeleteOrderClose}
        />
      )}
    </>
  );
};

export default ProspectsRoute;
